import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Button,
  Grid,
  MenuItem,
  FormControlLabel,
  Radio,
  RadioGroup,
  Box,
  CircularProgress,
  Typography,
  Link
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import AppModal from "components/AppModal";
import FlexBox from "components/flexbox/FlexBox";
import AppTextField from "components/input-fields/AppTextField";
import { H2, H6 } from "components/Typography";
import { useFormik } from "formik";
import * as Yup from "yup";
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { API_ENDPOINTS } from "Network/apiEndpoints";
import apiService from "Network/apiService";
import Select from '@mui/material/Select';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import OutlinedInput from '@mui/material/OutlinedInput';
import { cities } from "./indian_cities";
import CityDropdown from "./CityDropdown";
import { FileDownload } from "@mui/icons-material";
import { useToast } from "OtherReusableFunctionality/ToastContext";
import InputBase from '@mui/material/InputBase';
import SearchIcon from '@mui/icons-material/Search';
import Paper from '@mui/material/Paper';
import Delete from '@mui/icons-material/Delete';

const bloodGroups = ["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-"];
const genderOptions = [
    { value: "Male", label: "Male" },
    { value: "Female", label: "Female" },
    { value: "Other", label: "Other" }
];

const memberCategories = [
    { value: "president", label: "President" },
    { value: "vicePresident", label: "Vice President" },
    { value: "treasurer", label: "Treasurer" },
    { value: "member", label: "Member" }
];

const AddMemberModal = ({ open, onClose, edit, data, chapterId, creatorId, fetchMembers }) => {
    const toast = useToast();
    const [businessCategories, setBusinessCategories] = useState([]);
    const [maritalStatus, setMaritalStatus] = useState(data?.is_married ? "Married" : "Unmarried");
    const [showPassword, setShowPassword] = useState(false);
    const [editData, setEditData] = useState(null);
    const [isLoading, setIsLoading] = useState(false); 
    const [searchQuery, setSearchQuery] = useState('');
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        const fetchBusinessCategories = async () => {
            try {
                const response = await apiService.get(API_ENDPOINTS.GET_business_category);
                if (response?.success && response?.data) {
                    setBusinessCategories(response.data);
                }
            } catch (error) {
                console.error("Error fetching business categories:", error);
                toast.error("Error fetching business categories");
            }
        };

        fetchBusinessCategories();

        if (edit && data) {
            // Parse dates properly
            const parsedDOB = data.dob ? new Date(data.dob) : null;
            const parsedAnniversaryDate = data.anniversary_date ? new Date(data.anniversary_date) : null;

            // Handle business categories from API response
            const categoryIds = data.business_category?.map(cat => cat.id) || [];
            setFieldValue('business_category', categoryIds);
            setFieldValue('member_category', data.member_category || '');
            setFieldValue('full_name', data.full_name || '');
            setFieldValue('company_name', data.company_name || '');
            setFieldValue('company_turnover', data.company_turnover || '');
            setFieldValue('contact_no', data.contact_no || '');
            setFieldValue('email', data.email || '');
            setFieldValue('dob', parsedDOB);
            setFieldValue('blood_group', data.blood_group || '');
            setFieldValue('gender', data.gender || '');
            setFieldValue('is_married', data.is_married || false);
            setFieldValue('anniversary_date', parsedAnniversaryDate);
            setFieldValue('address', data.address || '');
            setFieldValue('city', data.city || '');
            setFieldValue('expectations_from_chapter', data.expectations_from_chapter || '');
            setFieldValue('information_from', data.information_from || '');
            setFieldValue('username', data.member_user?.username || '');
            // Don't set password in edit mode
            setMaritalStatus(data.is_married ? "Married" : "Unmarried");
            
            // Set the existing file path if it exists
            if (data.photo_maratha_validity) {
                setFieldValue('existing_photo_maratha_validity', data.photo_maratha_validity);
                setFieldError('photo_maratha_validity', undefined);
            }
        }
    }, [edit, data]);

    const validationSchema = Yup.object().shape({
        business_category: Yup.array()
            .min(1, "At least one Business Category is Required")
            .required("Business Category is Required"),
        member_category: Yup.string().required("Member Category is Required"),
        full_name: Yup.string().required("Full Name is Required"),
        company_name: Yup.string().required("Company Name is Required"),
        company_turnover: Yup.string().required("Company Turnover is Required"),
        contact_no: Yup.string()
            .matches(/^[0-9]{10}$/, "Invalid phone number")
            .required("Contact No is Required"),
        email: Yup.string().email("Invalid email").required("Email is Required"),
        // dob: Yup.date(),
        blood_group: Yup.string().required("Blood Group is Required"),
        address: Yup.string().required("Address is Required"),
        city: Yup.string().required("City is Required"),
        gender: Yup.string().required("Gender is Required"),
        password: edit ? Yup.string() : Yup.string().required("Password is Required"),
        // Make photo_maratha_validity optional by removing it from validation schema
    });

    const initialValues = {
        business_category: editData?.business_category || [],
        member_category: editData?.member_category || "",
        full_name: editData?.full_name || "",
        company_name: editData?.company_name || "",
        company_turnover: editData?.company_turnover || "",
        contact_no: editData?.contact_no || "",
        email: editData?.email || "",
        dob: editData?.dob || null,
        is_married: editData?.is_married || false,
        anniversary_date: editData?.anniversary_date || null,
        blood_group: editData?.blood_group || "",
        address: editData?.address || "",
        city: editData?.city || "",
        expectations_from_chapter: editData?.expectations_from_chapter || "",
        information_from: editData?.information_from || "",
        photo_maratha_validity: null,
        existing_photo_maratha_validity: null,
        username: editData?.username || "",
        password: editData?.password || "",
        gender: editData?.gender || "",
        registration_date: null,
        expiry_date: null,
    };

    const { values, errors, touched, handleChange, handleSubmit, setFieldValue, setFieldError } = useFormik({
        initialValues: initialValues,
        enableReinitialize: true,
        validationSchema,
        onSubmit: async (values) => {
            console.log(values);
            // Check for required dates
            // if (!values.registration_date) {
            //     toast.showError("Please select a Registration Date");
            //     return;
            // }
            // if (!values.expiry_date) {
            //     toast.showError("Please select an Expiry Date");
            //     return;
            // }
            

            console.log(values);
            const dobFormatted = values.dob ? new Date(values.dob).toISOString().split('T')[0] : null;
            if (dobFormatted === null) {
                toast.showError("Please select a Date of Birth");
                return;
            }
            
            const registrationDateFormatted = values.registration_date ? new Date(values.registration_date).toISOString().split('T')[0] : null;
            const expiryDateFormatted = values.expiry_date ? new Date(values.expiry_date).toISOString().split('T')[0] : null;
            setIsLoading(true);
            
            const formData = new FormData();
            
            // Handle multiple business categories
            if (values.business_category?.length > 0) {
                values.business_category.forEach((categoryId) => {
                    formData.append(`business_category`, categoryId);
                });
            }

            // Only include file in payload if a new file is selected
            if (values.photo_maratha_validity instanceof File) {
                formData.append('photo_maratha_validity', values.photo_maratha_validity);
            } else if (values.existing_photo_maratha_validity === null) {
                // If existing file was removed and no new file was selected
                formData.append('photo_maratha_validity', '');  // or however your API expects to handle file removal
            }

            // Add other fields to formData
            Object.keys(values).forEach(key => {
                if (key !== 'photo_maratha_validity' && 
                    key !== 'existing_photo_maratha_validity' && 
                    values[key] !== null && 
                    values[key] !== undefined) {
                    if (key === 'dob') {
                        formData.append(key, dobFormatted);
                    } else if (key === 'registration_date') {
                        formData.append(key, registrationDateFormatted);
                    } else if (key === 'expiry_date') {
                        formData.append(key, expiryDateFormatted);
                    } else if (key === 'anniversary_date') {
                        formData.append(key, values[key] ? new Date(values[key]).toISOString().split('T')[0] : '');
                    } else {
                        formData.append(key, values[key]);
                    }
                }
            });

            try {
                let response;

                if (edit) {
                    // Only include password in payload if it was changed
                    if (values.password) {
                        formData.append('password', values.password);
                    }
                    formData.append('member_id', data.id);
                    response = await apiService.put(API_ENDPOINTS.PUT_update_member, formData, "token","multipart/form-data");
                } else {
                    // For new members, include username and password
                    formData.append('username', values.username);
                    formData.append('password', values.password);
                    formData.append('user', JSON.parse(localStorage.getItem("logged_data") || "{}")?.user?.id);
                    formData.append('creator', JSON.parse(localStorage.getItem("logged_data") || "{}")?.user?.id);
                    formData.append('chapter', chapterId);
                    
                    response = await apiService.post(API_ENDPOINTS.POST_create_member, formData, "token","multipart/form-data");
                }

                if (response?.success) {
                    toast.showSuccess(`Member ${edit ? "Updated" : "Added"} Successfully`);
                    onClose();
                    fetchMembers();

                } else {
                    toast.showError(response?.message || `Failed to ${edit ? "update" : "add"} member`);
                } 
            } catch (error) {
                console.error("API error:", error);
            
                if (error?.message === "Username already exists.") {
                    toast.showError("Username already exists. Please choose a different username.");
                } else if (error?.message === "Email already exists.") {
                    toast.showError("Email already exists. Please use a different email.");
                } else if (error?.message === "Contact Number already exists.") {
                    toast.showError("Contact number already exists. Please use a different contact number.");
                }
                else {
                    console.log(error);
                    // toast.showError("An unexpected error occurred."); // General error
                }
                
                
            } finally {
                setIsLoading(false);
            }
        },
    });

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    const filteredCategories = useMemo(() => {
        return businessCategories.filter(category =>
            category.category_name.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [businessCategories, searchQuery]);

    return (
        <AppModal
            open={open}
            handleClose={onClose}
            sx={{
                "& .MuiPaper-root": {
                    backgroundColor: "background.paper",
                    maxWidth: 800,
                    width: "100%",
                    maxHeight: "100vh", //increase height
                    overflow: "hidden"
                }
            }}
        >
            <Box sx={{
                height: "70vh",
                overflow: "auto",
                p: 3,
                color: "text.primary"
            }}>
                <H2 mb={3}>{edit ? "Edit Member" : "Add New Member"}</H2>

                <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <H6 mb={2} sx={{ color: "text.secondary" }}>Basic Information</H6>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <AppTextField
                                        select
                                        fullWidth
                                        size="small"
                                        name="business_category"
                                        label="Business Category*"
                                        value={values.business_category || []}
                                        onChange={(event) => {
                                            const value = event.target.value;
                                            const newValue = typeof value === 'string' ? value.split(',') : value;
                                            setFieldValue('business_category', newValue);
                                        }}
                                        error={Boolean(errors.business_category && touched.business_category)}
                                        helperText={touched.business_category && errors.business_category}
                                        SelectProps={{
                                            multiple: true,
                                            open: menuOpen,
                                            onOpen: () => setMenuOpen(true),
                                            onClose: () => setMenuOpen(false),
                                            renderValue: (selected) => {
                                                if (!selected?.length) return '';
                                                return businessCategories
                                                    .filter(cat => selected.includes(cat.id))
                                                    .map(cat => cat.category_name)
                                                    .join(', ');
                                            },
                                            input: <OutlinedInput label="Business Category*" />,
                                            MenuProps: {
                                                PaperProps: {
                                                    style: {
                                                        maxHeight: 400
                                                    }
                                                }
                                            }
                                        }}
                                    >
                                        <Box sx={{ position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                                            <Paper sx={{ p: 1, m: 1, boxShadow: 0, border: '1px solid', borderColor: 'divider' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
                                                    <InputBase
                                                        placeholder="Search categories..."
                                                        value={searchQuery}
                                                        onChange={(e) => setSearchQuery(e.target.value)}
                                                        onClick={(e) => e.stopPropagation()}
                                                        sx={{ flex: 1 }}
                                                    />
                                                </Box>
                                            </Paper>
                                        </Box>
                                        <Box sx={{ 
                                            maxHeight: 300,
                                            overflow: 'auto',
                                            '&::-webkit-scrollbar': {
                                                width: '8px',
                                            },
                                            '&::-webkit-scrollbar-track': {
                                                background: '#f1f1f1',
                                                borderRadius: '4px',
                                            },
                                            '&::-webkit-scrollbar-thumb': {
                                                background: '#888',
                                                borderRadius: '4px',
                                            },
                                            '&::-webkit-scrollbar-thumb:hover': {
                                                background: '#555',
                                            },
                                        }}>
                                            {filteredCategories.map((category) => (
                                                <MenuItem 
                                                    key={category.id} 
                                                    value={category.id}
                                                    onClick={() => {
                                                        const currentValues = values.business_category || [];
                                                        const newValues = currentValues.includes(category.id)
                                                            ? currentValues.filter(id => id !== category.id)
                                                            : [...currentValues, category.id];
                                                        setFieldValue('business_category', newValues);
                                                    }}
                                                >
                                                    <Checkbox 
                                                        checked={values.business_category?.includes(category.id) || false}
                                                        color="primary"
                                                    />
                                                    <ListItemText primary={category.category_name} />
                                                </MenuItem>
                                            ))}
                                        </Box>
                                    </AppTextField>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <AppTextField
                                        fullWidth
                                        size="small"
                                        name="full_name"
                                        label="Full Name*"
                                        value={values.full_name}
                                        onChange={handleChange}
                                        error={Boolean(errors.full_name && touched.full_name)}
                                        helperText={touched.full_name && errors.full_name}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <AppTextField
                                        select
                                        fullWidth
                                        size="small"
                                        name="member_category"
                                        label="Member Category*"
                                        value={values.member_category}
                                        onChange={handleChange}
                                        error={Boolean(errors.member_category && touched.member_category)}
                                        helperText={touched.member_category && errors.member_category}
                                    >
                                        {memberCategories.map((category) => (
                                            <MenuItem key={category.value} value={category.value}>
                                                {category.label}
                                            </MenuItem>
                                        ))}
                                    </AppTextField>
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            <H6 mb={2} sx={{ color: "text.secondary" }}>Company Details</H6>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <AppTextField
                                        fullWidth
                                        size="small"
                                        name="company_name"
                                        label="Company Name*"
                                        value={values.company_name}
                                        onChange={handleChange}
                                        error={Boolean(errors.company_name && touched.company_name)}
                                        helperText={touched.company_name && errors.company_name}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <AppTextField
                                        fullWidth
                                        size="small"
                                        name="company_turnover"
                                        label="Company Turnover*"
                                        value={values.company_turnover}
                                        onChange={handleChange}
                                        error={Boolean(errors.company_turnover && touched.company_turnover)}
                                        helperText={touched.company_turnover && errors.company_turnover}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            <H6 mb={2} sx={{ color: "text.secondary" }}>Contact Information</H6>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <AppTextField
                                        fullWidth
                                        size="small"
                                        name="contact_no"
                                        label="Contact No*"
                                        value={values.contact_no}
                                        onChange={handleChange}
                                        error={Boolean(errors.contact_no && touched.contact_no)}
                                        helperText={touched.contact_no && errors.contact_no}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <AppTextField
                                        fullWidth
                                        size="small"
                                        name="email"
                                        type="email"
                                        label="Email*"
                                        value={values.email}
                                        onChange={handleChange}
                                        error={Boolean(errors.email && touched.email)}
                                        helperText={touched.email && errors.email}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            <H6 mb={2} sx={{ color: "text.secondary" }}>User Credentials</H6>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <AppTextField
                                        fullWidth
                                        size="small"
                                        name="username"
                                        label="Username*"
                                        value={values.username}
                                        onChange={handleChange}
                                        error={Boolean(errors.username && touched.username)}
                                        helperText={touched.username && errors.username}
                                        disabled={edit} // Disable in edit mode
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <AppTextField
                                        fullWidth
                                        size="small"
                                        type={showPassword ? 'text' : 'password'}
                                        name="password"
                                        label={edit ? "New Password (optional)" : "Password*"}
                                        value={values.password}
                                        onChange={handleChange}
                                        error={Boolean(errors.password && touched.password)}
                                        helperText={touched.password && errors.password}
                                        autoComplete="new-password"
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() => setShowPassword(!showPassword)}
                                                        edge="end"
                                                    >
                                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    {edit && (
                                        <Typography 
                                            variant="caption" 
                                            color="text.secondary" 
                                            sx={{ display: 'block', mt: 1 }}
                                        >
                                            🔒 For security, current password cannot be viewed. Leave blank to keep current password.
                                        </Typography>
                                    )}
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            <H6 mb={2} sx={{ color: "text.secondary" }}>Personal Details</H6>
                            <Grid container spacing={2}>


                                    <Grid item xs={12}>
                                        <H6 mb={2} sx={{ color: "primary.main" }}>Membership Dates</H6>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                    <DatePicker
                                                        label="Registration Date"
                                                        value={values.registration_date}
                                                        onChange={(newValue) => {
                                                            setFieldValue('registration_date', newValue);
                                                        }}
                                                        openTo="day"
                                                        views={['year', 'month', 'day']}
                                                        renderInput={(params) => (
                                                            <AppTextField
                                                                {...params}
                                                                fullWidth
                                                                size="small"
                                                            />
                                                        )}
                                                    />
                                                </LocalizationProvider>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                    <DatePicker
                                                        label="Expiry Date"
                                                        value={values.expiry_date}
                                                        onChange={(newValue) => {
                                                            setFieldValue('expiry_date', newValue);
                                                        }}
                                                        openTo="day"
                                                        views={['year', 'month', 'day']}
                                                        renderInput={(params) => (
                                                            <AppTextField
                                                                {...params}
                                                                fullWidth
                                                                size="small"
                                                            />
                                                        )}
                                                    />
                                                </LocalizationProvider>
                                            </Grid>
                                           
                                        </Grid>
                                    </Grid>

                                    
                                
                                <Grid item xs={12} md={6}>
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DatePicker
                                            label="Date of Birth*"
                                            value={values.dob}
                                            onChange={(newValue) => {
                                                setFieldValue('dob', newValue);
                                            }}
                                            renderInput={(params) => (
                                                <AppTextField
                                                    {...params}
                                                    fullWidth
                                                    size="small"
                                                    error={Boolean(errors.dob && touched.dob)}
                                                    helperText={touched.dob && errors.dob}
                                                />
                                            )}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <AppTextField
                                        select
                                        fullWidth
                                        size="small"
                                        name="blood_group"
                                        label="Blood Group*"
                                        value={values.blood_group}
                                        onChange={handleChange}
                                        error={Boolean(errors.blood_group && touched.blood_group)}
                                        helperText={touched.blood_group && errors.blood_group}
                                    >
                                        {bloodGroups.map((group) => (
                                            <MenuItem key={group} value={group}>
                                                {group}
                                            </MenuItem>
                                        ))}
                                    </AppTextField>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <AppTextField
                                        select
                                        fullWidth
                                        size="small"
                                        name="gender"
                                        label="Gender*"
                                        value={values.gender}
                                        onChange={handleChange}
                                        error={Boolean(errors.gender && touched.gender)}
                                        helperText={touched.gender && errors.gender}
                                    >
                                        {genderOptions.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </AppTextField>
                                </Grid>
                                <Grid item xs={12}>
                                    <H6 mb={1}>Marital Status</H6>
                                    <RadioGroup
                                        row
                                        name="is_married"
                                        value={maritalStatus}
                                        onChange={(e) => {
                                            setMaritalStatus(e.target.value);
                                        }}
                                    >
                                        <FormControlLabel
                                            value="Married"
                                            control={<Radio />}
                                            label="Married"
                                            sx={{ color: 'text.secondary' }}
                                        />
                                        <FormControlLabel
                                            value="Unmarried"
                                            control={<Radio />}
                                            label="Unmarried"
                                            sx={{ color: 'text.secondary' }}
                                        />
                                    </RadioGroup>
                                </Grid>

                                {maritalStatus === "Married" && (
                                    <Grid item xs={12} md={6}>
                                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                                            <DatePicker
                                                label="Anniversary Date"
                                                value={values.anniversary_date}
                                                onChange={(newValue) => {
                                                    setFieldValue('anniversary_date', newValue);
                                                }}
                                                renderInput={(params) => (
                                                    <AppTextField
                                                        {...params}
                                                        fullWidth
                                                        size="small"
                                                        error={Boolean(errors.anniversary_date && touched.anniversary_date)}
                                                        helperText={touched.anniversary_date && errors.anniversary_date}
                                                    />
                                                )}
                                            />
                                        </LocalizationProvider>
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            <H6 mb={2} sx={{ color: "text.secondary" }}>Address Information</H6>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <AppTextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        size="small"
                                        name="address"
                                        label="Address*"
                                        value={values.address}
                                        onChange={handleChange}
                                        error={Boolean(errors.address && touched.address)}
                                        helperText={touched.address && errors.address}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <CityDropdown
                                        name="city"
                                        label="City*"
                                        value={values.city}
                                        onChange={(value) => setFieldValue("city", value)}
                                        error={Boolean(errors.city && touched.city)}
                                        helperText={touched.city}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            <H6 mb={2} sx={{ color: "text.secondary" }}>Additional Information</H6>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <AppTextField
                                        fullWidth
                                        multiline
                                        rows={2}
                                        size="small"
                                        name="expectations_from_chapter"
                                        label="Expectation from Chapter"
                                        value={values.expectations_from_chapter}
                                        onChange={handleChange}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <AppTextField
                                        fullWidth
                                        size="small"
                                        name="information_from"
                                        label="How do you know about Maratha Udyojak"
                                        value={values.information_from}
                                        onChange={handleChange}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <H6 mb={1}>Documents</H6>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <Box>
                                                <AppTextField
                                                    fullWidth
                                                    size="small"
                                                    type="file"
                                                    name="photo_maratha_validity"
                                                    label="Proof Document (Optional)"
                                                    onChange={(event) => {
                                                        const file = event.currentTarget.files[0];
                                                        setFieldValue("photo_maratha_validity", file);
                                                    }}
                                                    error={Boolean(errors.photo_maratha_validity && touched.photo_maratha_validity)}
                                                    helperText={touched.photo_maratha_validity && errors.photo_maratha_validity}
                                                    InputLabelProps={{
                                                        shrink: true,
                                                    }}
                                                />

                                                {(values.photo_maratha_validity || (!values.photo_maratha_validity && data?.photo_maratha_validity && values.existing_photo_maratha_validity !== null)) && (
                                                    <Paper 
                                                        elevation={0}
                                                        sx={{ 
                                                            mt: 2,
                                                            p: 2,
                                                            border: '1px solid',
                                                            borderColor: 'divider',
                                                            borderRadius: 1,
                                                            bgcolor: 'background.paper',
                                                        }}
                                                    >
                                                        <FlexBox 
                                                            alignItems="center" 
                                                            justifyContent="space-between"
                                                            sx={{ flexWrap: 'wrap', gap: 1 }}
                                                        >
                                                            <FlexBox alignItems="center" gap={1}>
                                                                <FileDownload 
                                                                    fontSize="small" 
                                                                    sx={{ color: 'primary.main' }}
                                                                />
                                                                <Typography 
                                                                    variant="body2" 
                                                                    sx={{ 
                                                                        color: 'text.primary',
                                                                        fontWeight: 500
                                                                    }}
                                                                >
                                                                    {values.photo_maratha_validity 
                                                                        ? values.photo_maratha_validity.name 
                                                                        : 'Current Document'}
                                                                </Typography>
                                                            </FlexBox>

                                                            <FlexBox gap={1}>
                                                                {!values.photo_maratha_validity && data?.photo_maratha_validity && values.existing_photo_maratha_validity !== null && (
                                                                    <Button
                                                                        size="small"
                                                                        variant="outlined"
                                                                        color="primary"
                                                                        onClick={() => window.open(`${API_ENDPOINTS.BASE_URL}${data.photo_maratha_validity}`, '_blank')}
                                                                        startIcon={<FileDownload fontSize="small" />}
                                                                        sx={{
                                                                            borderRadius: 1,
                                                                            textTransform: 'none',
                                                                            minWidth: 'auto'
                                                                        }}
                                                                    >
                                                                        View
                                                                    </Button>
                                                                )}
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    color="error"
                                                                    onClick={() => {
                                                                        setFieldValue("photo_maratha_validity", null);
                                                                        setFieldValue("existing_photo_maratha_validity", null);
                                                                    }}
                                                                    startIcon={<Delete fontSize="small" />}
                                                                    sx={{
                                                                        borderRadius: 1,
                                                                        textTransform: 'none',
                                                                        minWidth: 'auto'
                                                                    }}
                                                                >
                                                                    Remove
                                                                </Button>
                                                            </FlexBox>
                                                        </FlexBox>
                                                    </Paper>
                                                )}

                                                {!values.photo_maratha_validity && !data?.photo_maratha_validity && values.existing_photo_maratha_validity === null && (
                                                    <Typography 
                                                        variant="caption" 
                                                        sx={{ 
                                                            display: 'block', 
                                                            mt: 1,
                                                            color: 'text.secondary',
                                                            fontStyle: 'italic'
                                                        }}
                                                    >
                                                        Supported formats: PDF, DOC, DOCX, PNG, JPG, JPEG
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>

                    <FlexBox justifyContent="flex-end" gap={2} mt={4}>
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            sx={{ minWidth: 120 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            sx={{ minWidth: 120 }}
                            disabled={isLoading} // Disable button while loading
                        >
                            {isLoading ? <CircularProgress size={24} /> : edit ? "Update" : "Add"} Member
                        </Button>
                    </FlexBox>
                </form>
            </Box>
        </AppModal>
    );
};

export default AddMemberModal;

